// TransportPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RightSideSummary from "./RightSideSummary";

// Validation utility functions
const validationRules = {
  fullName: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s.'-]+$/,
    message: "Please enter a valid full name (letters, spaces, apostrophes, hyphens only)"
  },
  companyName: {
    required: false, // We'll handle this dynamically based on userType
    minLength: 2,
    maxLength: 100,
    message: "Company name must be between 2 and 100 characters"
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Please enter a valid email address"
  },
  phone: {
    required: true,
    pattern: /^\(\d{3}\) \d{3}-\d{4}$/,
    message: "Please enter a valid 10-digit phone number"
  }
};

const validateField = (name, value, rule) => {
  if (rule.required && !value.trim()) {
    return `${name} is required`;
  }
  
  if (value.trim() && rule.minLength && value.trim().length < rule.minLength) {
    return `${name} must be at least ${rule.minLength} characters`;
  }
  
  if (value.trim() && rule.maxLength && value.trim().length > rule.maxLength) {
    return `${name} must be less than ${rule.maxLength} characters`;
  }
  
  if (value.trim() && rule.pattern && !rule.pattern.test(value.trim())) {
    return rule.message;
  }
  
  return "";
};

export default function TransportPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [updating, setUpdating] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  console.log(quoteData,'quoteData')
  const API_BASE_URL = 'https://www.gfapi.net' || ""; 
  const [userType, setUserType] = useState("individual");
  const [phoneNumbers, setPhoneNumbers] = useState([""]);
  const [phoneValues, setPhoneValues] = useState([""]);
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "tikerow212@emaxasp.com",
    companyName: ""
  });

  const normalizePhone = (val = "") =>
    val.replace(/\D/g, "").slice(0, 10);

  // Validate entire form
  const validateForm = () => {
    const errors = {};
    
    // Validate fullName
    const fullNameError = validateField(
      "Full name",
      formData.fullName,
      validationRules.fullName
    );
    if (fullNameError) errors.fullName = fullNameError;
    
    // Validate email
    const emailError = validateField(
      "Email",
      formData.email,
      validationRules.email
    );
    if (emailError) errors.email = emailError;
    
    // Validate company name if business user
    if (userType === "business") {
      // Create a company rule with required: true for business users
      const companyRule = {
        ...validationRules.companyName,
        required: true
      };
      const companyNameError = validateField(
        "Company name",
        formData.companyName,
        companyRule
      );
      if (companyNameError) errors.companyName = companyNameError;
    } else {
      // For individual users, company name is optional but should be validated if provided
      if (formData.companyName.trim()) {
        const companyNameError = validateField(
          "Company name",
          formData.companyName,
          validationRules.companyName
        );
        if (companyNameError) errors.companyName = companyNameError;
      }
    }
    
    // Validate primary phone
    const primaryPhoneError = validateField(
      "Phone number",
      phoneValues[0],
      validationRules.phone
    );
    if (primaryPhoneError) errors.phone = primaryPhoneError;
    
    // Validate additional phones (optional but must be valid if provided)
    phoneValues.slice(1).forEach((phone, index) => {
      if (phone.trim()) {
        const phoneError = validateField(
          `Phone number ${index + 2}`,
          phone,
          validationRules.phone
        );
        if (phoneError) errors[`phone_${index + 2}`] = phoneError;
      }
    });
    
    return errors;
  };

  // Field blur handler
  const handleBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    // Validate specific field
    if (field === 'fullName') {
      const error = validateField("Full name", formData.fullName, validationRules.fullName);
      setValidationErrors(prev => ({ ...prev, fullName: error }));
    } else if (field === 'companyName') {
      // Use appropriate validation rule based on userType
      const companyRule = userType === "business" 
        ? { ...validationRules.companyName, required: true }
        : validationRules.companyName;
      
      const error = validateField("Company name", formData.companyName, companyRule);
      setValidationErrors(prev => ({ ...prev, companyName: error }));
    } else if (field === 'email') {
      const error = validateField("Email", formData.email, validationRules.email);
      setValidationErrors(prev => ({ ...prev, email: error }));
    } else if (field.startsWith('phone_')) {
      const index = parseInt(field.split('_')[1]);
      const phone = phoneValues[index] || "";
      if (phone.trim()) {
        const error = validateField(
          `Phone number ${index + 1}`,
          phone,
          validationRules.phone
        );
        setValidationErrors(prev => ({ ...prev, [field]: error }));
      }
    }
  };

  // Clear company name validation when switching from business to individual
  useEffect(() => {
    if (userType === "individual" && validationErrors.companyName) {
      setValidationErrors(prev => ({ ...prev, companyName: "" }));
    }
  }, [userType]);

  /* ================= GET QUOTE ================= */
  useEffect(() => {
    if (!id) return;

    const fetchQuote = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/quotes/${id}`
        );
       
        if (!res.ok) throw new Error("Failed to load quote");

        const json = await res.json();
        if (!json?.success) throw new Error("Quote not found");

        setQuoteData(json.data);

        // Pre-fill form data if available
        if (json.data?.contact) {
          const contact = json.data.contact;
          
          // Set user type
          if (contact.userType) {
            setUserType(contact.userType);
          }
          
          // Pre-fill form data
          const newFormData = {
            fullName: contact.fullName || `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
            email: contact.email || "tikerow212@emaxasp.com",
            companyName: contact.companyName || ""
          };
          setFormData(newFormData);
          
          // Pre-fill phone numbers
          if (contact.phones && contact.phones.length > 0) {
            const formattedPhones = contact.phones.map(phone => {
              const digits = phone.replace(/\D/g, "");
              if (digits.length === 10) {
                return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
              }
              return "";
            });
            setPhoneValues(formattedPhones);
            setPhoneNumbers(formattedPhones.map(() => ""));
          } else if (contact.phone) {
            const digits = contact.phone.replace(/\D/g, "");
            if (digits.length === 10) {
              const formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
              setPhoneValues([formatted]);
              setPhoneNumbers([""]);
            }
          }
        }
      } catch (err) {
        console.error("TransportPage GET quote error:", err);
        alert("Failed to load quote data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  // Handle phone number input with formatting
  const handlePhoneChange = (index, value) => {
    const digits = value.replace(/\D/g, "");
    
    let formatted = "";
    if (digits.length > 0) {
      formatted = "(" + digits.substring(0, 3);
    }
    if (digits.length >= 4) {
      formatted += ") " + digits.substring(3, 6);
    }
    if (digits.length >= 7) {
      formatted += "-" + digits.substring(6, 10);
    }
    
    const newPhoneValues = [...phoneValues];
    newPhoneValues[index] = formatted;
    setPhoneValues(newPhoneValues);
    
    // Clear validation error for this phone
    setValidationErrors(prev => ({ ...prev, [`phone_${index}`]: "" }));
  };

  const addPhoneNumber = () => {
    setPhoneNumbers([...phoneNumbers, ""]);
    setPhoneValues([...phoneValues, ""]);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle user type change
  const handleUserTypeChange = (type) => {
    setUserType(type);
    // Clear company name validation when switching types
    if (type === "individual" && validationErrors.companyName) {
      setValidationErrors(prev => ({ ...prev, companyName: "" }));
    }
  };

  // Handle continue to pickup
  const handleContinue = async () => {
    if (!quoteData?._id || updating) return;
    
    // Validate form
    const errors = validateForm();
    setValidationErrors(errors);
    
    // Mark all relevant fields as touched
    const touched = {
      fullName: true,
      email: true,
      phone_0: true,
    };
    
    // Only mark companyName as touched if it's business user or has content
    if (userType === "business" || formData.companyName.trim()) {
      touched.companyName = true;
    }
    
    setTouchedFields(touched);
    
    // Check if there are any errors
    if (Object.keys(errors).some(key => errors[key])) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors).find(key => errors[key]);
      const element = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    setUpdating(true);

    // split full name safely
    const nameParts = formData.fullName.trim().split(" ");
    const firstName = nameParts.shift() || "";
    const lastName = nameParts.join(" ") || "";

    const payload = {
      contact: {
        userType,
        companyName: userType === "business" ? formData.companyName.trim() : "",
        fullName: formData.fullName.trim(),
        firstName,
        lastName,
        email: formData.email.trim(),
        phone: normalizePhone(phoneValues[0] || ""),
        phones: phoneValues
          .map(normalizePhone)
          .filter((p) => p.length === 10),
        consent: true,
      },
    };

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/quotes/${quoteData._id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const txt = await res.text();
        console.error("Update failed:", txt);
        alert("Unable to continue. Please try again.");
        return;
      }

      const json = await res.json();
      if (!json?.success) {
        alert("Update failed. Please try again.");
        return;
      }

      // ✅ SUCCESS → NEXT STEP
      navigate(`/pickup/${quoteData._id}`);
    } catch (err) {
      console.error("Transport update error:", err);
      alert("Network error. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  // Helper to show error message
  const renderError = (field) => {
    if (validationErrors[field] && (touchedFields[field] || updating)) {
      return (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {validationErrors[field]}
        </p>
      );
    }
    return null;
  };

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A1EF]" />
      </div>
    );
  }


  console.log(quoteData,'quoteData')
  
  const formatDate = (date) => {
  if (!date) return null;

  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// main logic
const pickupDate = quoteData?.pickupDate;

// agar user ki date full ho / option "date" ho to usi ko pick karo
// warna first available date use karo
const firstAvailableDate =
  pickupDate?.option === 'date'
    ? formatDate(pickupDate.date)
    : formatDate(quoteData?.firstAvailableDate);

  /* ================= RIGHT SIDE DATA ================= */
  const transportBookingSummary = quoteData ? {
    distance: quoteData.distanceMiles
      ? `${quoteData.distanceMiles.toFixed(1)} mi`
      : "N/A",
      firstAvailableDate,
    shipFrom: `${quoteData.pickup?.city || ""}, ${quoteData.pickup?.state || ""} ${quoteData.pickup?.zip || ""}`,
    shipTo: `${quoteData.dropoff?.city || ""}, ${quoteData.dropoff?.state || ""} ${quoteData.dropoff?.zip || ""}`,
    transportType:
      quoteData.carrier === "enclosed" ? "Enclosed" : "Open",
    serviceType: quoteData.bookingMeta?.serviceType || "Door to Door",
    insurance: quoteData.bookingMeta?.insurance || "Included",
    transitTime: quoteData.bookingMeta?.transitTime || "N/A",
  } : {};

    console.log(transportBookingSummary,'transportBookingSummary')
  const v = quoteData?.vehicles?.[0];

  const transportVehicleDetails = quoteData ? {
    vehicleMake: `${v?.year || ""} ${v?.make || ""}`.trim(),
    vehicleModel: v?.model || "",
    vehicleCondition: v?.isOperable ? "Running" : "Non-Running"
  } : {};

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <div className="p-4 md:p-8 max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
        {/* LEFT SIDE - FORM */}
        <div className="flex-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-[#103558] mb-2">Transport</h1>
            <p className="text-gray-600 mb-6">
              A couple more specifics, we want to get everything right!
            </p>

            {/* YOU ARE SECTION */}
            <div className="mb-6">
              <p className="text-sm font-medium text-[#103558] mb-3">You are</p>

              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => handleUserTypeChange("individual")}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    userType === "individual"
                      ? "border-[#00A1EF] bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    userType === "individual"
                      ? "border-[#00A1EF] bg-[#00A1EF]"
                      : "border-gray-300"
                  }`}>
                    {userType === "individual" && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <span className="text-sm text-gray-700">An individual</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleUserTypeChange("business")}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    userType === "business"
                      ? "border-[#00A1EF] bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    userType === "business"
                      ? "border-[#00A1EF] bg-[#00A1EF]"
                      : "border-gray-300"
                  }`}>
                    {userType === "business" && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <span className="text-sm text-gray-700">Representing a business</span>
                </button>
              </div>

              {userType === "business" && (
                <div className="mb-4" data-field="companyName">
                  <label className="block text-sm font-medium text-[#103558] mb-1">
                    Company name *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange("companyName", e.target.value)}
                    onBlur={() => handleBlur("companyName")}
                    placeholder="Enter company name"
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                      validationErrors.companyName && (touchedFields.companyName || updating)
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#00A1EF]"
                    }`}
                    required
                  />
                  {renderError("companyName")}
                </div>
              )}
            </div>

            {/* FORM FIELDS */}
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              {/* FULL NAME */}
              <div data-field="fullName">
                <label className="block text-sm font-medium text-[#103558] mb-1">
                  Your full name *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange("fullName", e.target.value)}
                  onBlur={() => handleBlur("fullName")}
                  placeholder="e.g. John Doe"
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                    validationErrors.fullName && touchedFields.fullName
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-[#00A1EF]"
                  }`}
                  required
                />
                {renderError("fullName")}
              </div>

              {/* EMAIL */}
              <div data-field="email">
                <label className="block text-sm font-medium text-[#103558] mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                  readOnly
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg ${
                    validationErrors.email && touchedFields.email
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300 bg-gray-50"
                  }`}
                />
                {renderError("email")}
                <p className="text-xs text-gray-500 mt-1">
                  Pre-filled from your account
                </p>
              </div>

              {/* PHONE NUMBERS */}
              <div>
                <label className="block text-sm font-medium text-[#103558] mb-1">
                  Phone number{phoneNumbers.length > 1 ? "s" : ""} *
                </label>

                {phoneNumbers.map((_, index) => (
                  <div key={index} className="mb-2" data-field={`phone_${index}`}>
                    <span className="text-xs text-gray-600">
                      Phone number {index + 1} {index === 0 ? "*" : ""}
                    </span>
                    <input
                      type="tel"
                      value={phoneValues[index]}
                      onChange={(e) => handlePhoneChange(index, e.target.value)}
                      onBlur={() => handleBlur(`phone_${index}`)}
                      placeholder="(___)-___-____"
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                        validationErrors[`phone_${index}`] && (touchedFields[`phone_${index}`] || index === 0)
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-[#00A1EF]"
                      }`}
                      maxLength="14"
                      required={index === 0}
                    />
                    {renderError(`phone_${index}`)}
                  </div>
                ))}
              </div>

              <div
                className="text-xs text-[#00A1EF] font-medium mb-4 cursor-pointer hover:text-[#0088CC] inline-flex items-center gap-1"
                onClick={addPhoneNumber}
              >
                <span className="text-base">+</span> Add another number (Optional)
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  By providing your phone number and clicking through, you agree
                  to our Terms, Privacy Policy, and authorize us to make or
                  initiate sales calls, text messages, and prerecorded
                  voicemails to that number using an automated system. Your
                  agreement is not a condition of purchasing products, goods or
                  services. You may opt out at any time.
                </p>

                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={updating}
                  className={`w-full px-4 py-3 rounded-lg shadow transition-all duration-200 text-sm flex items-center justify-center gap-2
                    ${
                      updating
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-[#00A1EF] to-[#0088CC] hover:from-[#0088CC] hover:to-[#0077B3] text-white"
                    }
                  `}
                >
                  {updating ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue to Pickup Info
                      <span className="text-lg">→</span>
                    </>
                  )}
                </button>
                
                {/* Form validation summary */}
                {Object.keys(validationErrors).some(key => validationErrors[key]) && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm font-medium flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Please fix the errors above before continuing
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* RIGHT SIDE – SAME COMPONENT, REAL DATA */}
        <RightSideSummary
          title="Details"
          bookingSummary={transportBookingSummary}
          vehicleDetails={transportVehicleDetails}
          editable={false}
          showPromoCode={true}
          priceDetails={{
            dueNow: "$0",
            totalDue:
              quoteData?.pricing?.selectedPriceType === "discount"
                ? `$${quoteData?.pricing?.discountPrice ?? "0"}`
                : `$${quoteData?.pricing?.regularPrice ?? "0"}`,
            priceLabel:
              quoteData?.pricing?.selectedPriceType === "discount"
                ? "Discount price"
                : "Regular price"
          }}
        />
      </div>
    </div>
  );
}